import { useState, useEffect, useCallback } from 'react';
import { useSearchBookings } from '../../../../api/services/lupp/test-drive/test-drive';

export type BookingFilters = {
  testDriveId?: string;
  status?: string;
  vehicleModel?: string;
  vin?: string;
  testDriveType?: string;
  locationName?: string;
  appointmentStartDate?: string;
  appointmentEndDate?: string;
  appointmentEndFrom?: string;
  appointmentEndTo?: string;
};

type UseBookingSearchTableParams = {
  /** Pre-seeds the prospect filter, e.g. when navigated to from a prospect's Test Drive tab. */
  initialProspectId?: string;
  /** Pre-seeds the filter panel values from URL query params. */
  initialFilters?: BookingFilters;
  enabled?: boolean;
};

const EMPTY_FILTERS: BookingFilters = {};

// Columns whose key isn't the API's sortBy field.
const SORT_BY_FIELD: Record<string, string> = {
  appointmentStart: 'scheduledStartDatetime',
  appointmentEnd: 'scheduledEndDatetime',
  prospectName: 'prospectFullName',
  host: 'hostName',
};

/**
 * Custom hook for managing the booking search table state (pagination, filters, sort).
 * @param params - Hook parameters
 * @returns Table state and handlers
 */
export const useBookingSearchTable = ({
  initialProspectId,
  initialFilters,
  enabled = true,
}: UseBookingSearchTableParams = {}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [prospectId, setProspectId] = useState<string | undefined>(initialProspectId);
  const [filters, setFilters] = useState<BookingFilters>(initialFilters ?? EMPTY_FILTERS);
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  // Scroll to top when page changes
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const {
    data: bookingsData,
    isLoading,
    isFetching,
    error,
  } = useSearchBookings({
    prospectId,
    searchText: searchText.trim() || undefined,
    testDriveId: filters.testDriveId,
    status: filters.status,
    vehicleModel: filters.vehicleModel,
    vin: filters.vin,
    testDriveType: filters.testDriveType,
    locationName: filters.locationName,
    appointmentStartDate: filters.appointmentStartDate,
    appointmentEndDate: filters.appointmentEndDate,
    appointmentEndFrom: filters.appointmentEndFrom,
    appointmentEndTo: filters.appointmentEndTo,
    pageNumber: currentPage,
    pageSize,
    sortBy: sortField ? (SORT_BY_FIELD[sortField] ?? sortField) : undefined,
    sortDirection: sortOrder ? (sortOrder.toUpperCase() as 'ASC' | 'DESC') : undefined,
    enabled,
  });

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  }, []);

  const handleFiltersChange = useCallback((newFilters: BookingFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setProspectId(undefined);
    setCurrentPage(0);
  }, []);

  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortField(column);
    setSortOrder(direction);
    setCurrentPage(0);
  }, []);

  return {
    // Data
    data: bookingsData?.bookings || [],
    totalCount: bookingsData?.totalCount || 0,
    isLoading,
    isFetching,
    error,

    // Pagination state
    currentPage,
    pageSize,

    // Filter state
    filters,
    searchText,
    prospectId,

    // Sort state
    sortField,
    sortOrder,

    // Handlers
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onFiltersChange: handleFiltersChange,
    onSearchChange: handleSearchChange,
    onClearFilters: handleClearFilters,
    onSort: handleSort,
  };
};
